import { ComponentProps, useMemo, useState } from 'react'

import { WorkDay } from '@/entities/schedules/models/WorkDay'
import { Button, Icon } from '@/shared/components'
import { pxToRem } from '@/shared/styles/themes'
import { faCopy, faToggleOn } from '@fortawesome/pro-regular-svg-icons'
import { Box, FormHelperText, Grid, styled, Typography } from '@mui/material'
import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium'

import { CustomHeader } from '../GridTable/CustomHeader/CustomHeader'
import { TimeEditCell } from '../GridTable/GridCells/TimeEditCell'
import { GridTable } from '../GridTable/GridTable'
import { CustomIcon } from '../Icon/CustomIcon'

export type WorkingHoursFieldProps = Pick<ComponentProps<typeof StyledContainer>, 'sx'> & {
  value: WorkDay[]
  disabled?: boolean
  error?: boolean
  onChange: (value: WorkDay[]) => void
}

export const WorkingHoursField = ({
  value,
  error,
  disabled,
  onChange,
  ...props
}: WorkingHoursFieldProps) => {
  const columns: GridColDef[] = [
    {
      field: 'dayOfWeek',
      headerName: 'Weekday',
      width: 80,
      editable: false,
    },
    // Earliest Start Group
    {
      field: 'startTimeEarliest',
      headerName: 'Time (*)',
      width: 100,
      editable: !disabled,
      renderEditCell: (params) => (
        <TimeEditCell
          {...params}
          required
          placeholder="HH:mm"
          format="HH:mm"
        />
      ),
    },
    {
      field: 'startTimeEarliestNextDay',
      headerName: 'ND?',
      width: 40,
      editable: !disabled,
      type: 'boolean',
    },
    // Latest Start Group
    {
      field: 'startTimeLatest',
      headerName: 'Time',
      width: 100,
      editable: !disabled,
      renderEditCell: (params) => (
        <TimeEditCell
          {...params}
          placeholder="HH:mm"
          format="HH:mm"
        />
      ),
    },
    {
      field: 'startTimeLatestNextDay',
      headerName: 'ND?',
      width: 40,
      editable: !disabled,
      type: 'boolean',
    },
    // Latest Finish Group
    {
      field: 'finishTimeLatest',
      headerName: 'Time',
      width: 100,
      editable: !disabled,
      renderEditCell: (params) => (
        <TimeEditCell
          {...params}
          placeholder="HH:mm"
          format="HH:mm"
        />
      ),
    },
    {
      field: 'finishTimeLatestNextDay',
      headerName: 'ND?',
      width: 40,
      editable: !disabled,
      type: 'boolean',
    },
    // Earliest Load Group
    {
      field: 'loadTimeEarliest',
      headerName: 'Time',
      width: 100,
      editable: !disabled,
      renderEditCell: (params) => (
        <TimeEditCell
          {...params}
          placeholder="HH:mm"
          format="HH:mm"
        />
      ),
    },
    {
      field: 'loadTimeEarliestNextDay',
      headerName: 'ND?',
      width: 40,
      editable: !disabled,
      type: 'boolean',
    },
    // Latest Load Group
    {
      field: 'loadTimeLatest',
      headerName: 'Time',
      width: 100,
      editable: !disabled,
      renderEditCell: (params) => (
        <TimeEditCell
          {...params}
          placeholder="HH:mm"
          format="HH:mm"
        />
      ),
    },
    {
      field: 'loadTimeLatestNextDay',
      headerName: 'ND?',
      width: 40,
      editable: !disabled,
      type: 'boolean',
    },
    // Max Time Fields
    {
      field: 'maxDrivingTime',
      headerName: 'Driving',
      width: 100,
      editable: !disabled,
      renderEditCell: (params) => (
        <TimeEditCell
          {...params}
          placeholder="HH:mm"
          format="HH:mm"
        />
      ),
    },
    {
      field: 'maxWorkingTime',
      headerName: 'Working',
      width: 85,
      editable: !disabled,
      renderEditCell: (params) => (
        <TimeEditCell
          {...params}
          placeholder="HH:mm"
          format="HH:mm"
        />
      ),
    },
    {
      field: 'minPaidTime',
      headerName: 'Min. Paid Time (S)',
      description: 'Minimum Time for calculating Cost if Run is used on the Runsheet',
      width: 140,
      editable: !disabled,
      renderEditCell: (params) => (
        <TimeEditCell
          {...params}
          placeholder="HH:mm"
          format="HH:mm"
        />
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      description: 'Active if Run is available on the Weekday',
      width: 75,
      editable: !disabled,
      type: 'boolean',
      valueGetter: (val) => val ? 'Active' : 'Disabled',
    },
  ]

  const columnGroupingModel: GridColumnGroupingModel = [
    {
      groupId: 'startTimeE',
      headerName: 'Earliest Start',
      children: [
        { field: 'startTimeEarliest' },
        { field: 'startTimeEarliestNextDay' },
      ],
    },
    {
      groupId: 'startTimeL',
      headerName: 'Latest Start',
      children: [
        { field: 'startTimeLatest' },
        { field: 'startTimeLatestNextDay' },
      ],
      renderHeaderGroup: (params) => {
        return <CustomHeader
          endIcon={<StyledSolverIcon iconCode="solver" />}
          {...params}
        />
      },
    },
    {
      groupId: 'finishTimeL',
      headerName: 'Latest Finish',
      children: [
        { field: 'finishTimeLatest' },
        { field: 'finishTimeLatestNextDay' },
      ],
      renderHeaderGroup: (params) => {
        return <CustomHeader
          endIcon={<StyledSolverIcon iconCode="solver" />}
          {...params}
        />
      },
    },
    {
      groupId: 'loadTimeE',
      headerName: 'Earliest Load',
      children: [
        { field: 'loadTimeEarliest' },
        { field: 'loadTimeEarliestNextDay' },
      ],
      renderHeaderGroup: (params) => {
        return <CustomHeader
          endIcon={<StyledSolverIcon iconCode="solver" />}
          {...params}
        />
      },
    },
    {
      groupId: 'loadTimeL',
      headerName: 'Latest Load',
      children: [
        { field: 'loadTimeLatest' },
        { field: 'loadTimeLatestNextDay' },
      ],
      renderHeaderGroup: (params) => {
        return <CustomHeader
          endIcon={<StyledSolverIcon iconCode="solver" />}
          {...params}
        />
      },
    },
    {
      groupId: 'maxTime',
      headerName: 'Max Time',
      children: [
        { field: 'maxDrivingTime' },
        { field: 'maxWorkingTime' },
      ],
    },
  ]

  const rows = useMemo(() => {
    if (!value) return []

    // Move Sunday (weekday 0) to the end
    const sortedWorkDays = [...value].sort((a, b) => {
      if (a.weekday === 0) return 1
      if (b.weekday === 0) return -1

      return a.weekday - b.weekday
    })

    return sortedWorkDays.map((workDay) => ({
      ...workDay,
      isSelectable: true,
    }))
  }, [value])

  const [selectedWeekday, setSelectedWeekday] = useState<number | null>(null)
  const [copiedWeekday, setCopiedWeekday] = useState<number | null>(null)

  const handleCopyWeekday = () => {
    if (selectedWeekday !== null) {
      setCopiedWeekday(selectedWeekday)
    }
  }

  const handlePasteWeekday = () => {
    if (copiedWeekday !== null && selectedWeekday !== null && copiedWeekday !== selectedWeekday) {
      const newWorkDays = value.map(workDay => {
        if (workDay.weekday === selectedWeekday) {
          const sourceDayData = value.find(w => w.weekday === copiedWeekday)

          if (sourceDayData) {
            return {
              ...sourceDayData,
              id: workDay.id,
              weekday: workDay.weekday,
              dayOfWeek: workDay.dayOfWeek,
            }
          }
        }

        return workDay
      })

      onChange(newWorkDays)
    }
  }

  const handleToggleStatus = () => {
    if (selectedWeekday !== null) {
      const newWorkDays = value.map(workDay => {
        if (workDay.weekday === selectedWeekday) {
          return {
            ...workDay,
            isActive: !workDay.isActive,
          }
        }

        return workDay
      })

      onChange(newWorkDays)
    }
  }

  return (
    <StyledContainer {...props}>
      <Grid container justifyContent="space-between" alignItems="center" mb={1}>
        <Grid item>
          <Typography
            variant="body1"
            sx={{
              fontSize: pxToRem(16),
              fontWeight: 500,
              mt: 1,
              mb: 1,
            }}
          >
            Working Hours Table
          </Typography>
        </Grid>
        <Grid item>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              text
              round
              disabled={disabled || selectedWeekday === null}
              startIcon={<Icon icon={faCopy} />}
              onClick={handleCopyWeekday}
            >
              Copy Weekday
            </Button>
            <Button
              text
              round
              disabled={disabled || selectedWeekday === null || copiedWeekday === null}
              startIcon={<Icon icon={faCopy} />}
              onClick={handlePasteWeekday}
            >
              Paste Weekday
            </Button>
            <Button
              text
              round
              disabled={disabled || selectedWeekday === null}
              startIcon={<Icon icon={faToggleOn} />}
              onClick={handleToggleStatus}
            >
              Toggle Status
            </Button>
          </Box>
        </Grid>
      </Grid>

      <StyledGridTable
        columns={columns}
        rows={rows}
        getRowId={(row) => row.weekday}
        columnGroupingModel={columnGroupingModel}
        onRowSelectionModelChange={(model) => {
          setSelectedWeekday(model.length > 0 ? model[0] as number : null)
        }}
        processRowUpdate={(newRow) => {
          const newWorkDays = value.map(workDay => {
            if (workDay.weekday === newRow.weekday) {
              return {
                ...workDay,
                ...newRow,
              }
            }

            return workDay
          })

          onChange(newWorkDays)

          return newRow
        }}
        slots={{
          footer: () => null,
        }}
      />
      <StyledHelperText error={error}>
        {error ? 'Please ensure all required fields are filled' : (!disabled ? 'Double-click cell to update value' : '')}
      </StyledHelperText>
    </StyledContainer>
  )
}

const StyledContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: ${(props) => props.theme.typography.pxToRem(480)};
`

const StyledHelperText = styled(FormHelperText)`
  margin-bottom: ${(props) => props.theme.typography.pxToRem(8)};
`

const StyledSolverIcon = styled(CustomIcon)`
  width: ${(props) => props.theme.typography.pxToRem(18)};
  height: ${(props) => props.theme.typography.pxToRem(18)};
  margin-left: ${(props) => props.theme.typography.pxToRem(4)};
`

const StyledGridTable = styled(GridTable)`
  height: 100%;
`